import type { Segment } from '$lib/stores/aiFeatures'
import { AIProviderFactory, type AIProvider, type AIProviderConfig } from '$lib/ai'
import { settingsStore } from '$lib/stores/settings'
import { get } from 'svelte/store'
import { AIAnalysisService } from './ai/AIAnalysisService'
import { AIClientManagerService } from './ai/analysis/AIClientManagerService'
import { usageTracker } from './UsageTrackingService'
import { showUpgradePrompt, upgradePromptContext, subscriptionStore } from '$lib/stores/subscription'
import { demoTracker } from '$lib/stores/demoStore'

interface AIRequest {
  mode: 'paraphrase' | 'expand' | 'shrink' | 'summarize' | 'continue' | 'critic'
  text: string
  context?: string
  options?: Record<string, any>
}

interface AIResponse {
  success: boolean
  result?: string | string[]
  reason?: string
  error?: string
}

export class AIService {
  private static apiEndpoint = '/api/ai'
  private static provider: AIProvider | null = null
  private static providerCache: Map<string, AIProvider> = new Map() // Cache for model-specific providers
  private static aiAnalysisService: AIAnalysisService | null = null
  private static statusCallback: ((message: string, type?: 'info' | 'warning' | 'error' | 'success') => void) | null = null
  private static systemPromptStoreCache: any = null // Cache for systemPromptStore
  private static postingPromptStoreCache: any = null // Cache for postingPromptStore
  private static abortController: AbortController | null = null // For cancelling ongoing requests
  
  /**
   * Get system prompt store (cached)
   */
  private static async getSystemPromptStore() {
    if (!this.systemPromptStoreCache) {
      this.systemPromptStoreCache = await import('$lib/stores/systemPrompt').then(m => m.systemPromptStore)
    }
    return this.systemPromptStoreCache
  }
  
  /**
   * Get posting prompt store (cached)
   */
  private static async getPostingPromptStore() {
    if (!this.postingPromptStoreCache) {
      this.postingPromptStoreCache = await import('$lib/stores/postingPrompt').then(m => m.postingPromptStore)
    }
    return this.postingPromptStoreCache
  }
  
  /**
   * Get combined prompts with posting taking priority
   */
  private static async getCombinedPrompt(): Promise<string> {
    const [systemPromptStore, postingPromptStore] = await Promise.all([
      this.getSystemPromptStore(),
      this.getPostingPromptStore()
    ])
    
    const systemPrompt = systemPromptStore.getPrompt()
    const postingPromptData = postingPromptStore.get()
    const postingPrompt = postingPromptData?.prompt
    
    // Combine prompts with posting taking priority
    if (postingPrompt && postingPrompt.trim()) {
      if (systemPrompt && systemPrompt.trim()) {
        // Both exist - posting takes priority, system is secondary
        return `주요 스타일 (최우선 적용): ${postingPrompt}\n\n보조 스타일 (상충 시 무시): ${systemPrompt}`
      }
      // Only posting prompt exists
      return postingPrompt
    } else if (systemPrompt && systemPrompt.trim()) {
      // Only system prompt exists
      return systemPrompt
    }
    
    return ''
  }
  
  /**
   * Get abbreviated model name for display
   */
  private static getAbbreviatedModelName(modelId: string): string {
    if (!modelId) return 'AI'
    
    // Common models - return short names
    if (modelId.includes('gpt-4o-mini')) return 'GPT-4o Mini'
    if (modelId.includes('gpt-4o')) return 'GPT-4o'
    if (modelId.includes('claude-3-5-sonnet')) return 'Claude Sonnet'
    if (modelId.includes('claude-3-5-haiku')) return 'Claude Haiku'
    if (modelId.includes('claude-3-opus')) return 'Claude Opus'
    if (modelId.includes('gemini-1.5-pro')) return 'Gemini Pro'
    if (modelId.includes('gemini-1.5-flash')) return 'Gemini Flash'
    if (modelId.includes('solar')) return 'Solar'
    if (modelId.includes('llama')) return 'Llama'
    if (modelId.includes('mixtral')) return 'Mixtral'
    
    // OpenRouter format: take model name after slash
    if (modelId.includes('/')) {
      const parts = modelId.split('/')
      const modelName = parts[parts.length - 1]
      // Take first two parts of model name
      return modelName.split('-').slice(0, 2).join(' ')
    }
    
    // Default: take first two parts
    return modelId.split('-').slice(0, 2).join(' ')
  }
  
  /**
   * Set status message callback
   */
  static setStatusCallback(callback: (message: string, type?: 'info' | 'warning' | 'error' | 'success') => void) {
    this.statusCallback = callback
  }
  
  /**
   * Show status message if callback is set
   */
  private static showStatus(message: string, type: 'info' | 'warning' | 'error' | 'success' = 'info') {
    if (this.statusCallback) {
      this.statusCallback(message, type)
    }
  }

  /**
   * Check demo mode limits for AI features
   */
  private static checkDemoLimits(feature: string): void {
    if (demoTracker.isDemoMode()) {
      const checkResult = demoTracker.useAI(feature as any)
      if (!checkResult.success) {
        // Dispatch event to show demo expired modal
        window.dispatchEvent(new CustomEvent('demo-expired', {
          detail: { feature: 'ai' }
        }))
        throw new Error('AI 체험이 종료되었습니다')
      }

      // Show warning if needed
      if (checkResult.showWarning) {
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: {
            message: `AI 기능 ${checkResult.remaining}회 남았습니다`,
            type: 'warning'
          }
        }))
      }
    }
  }

  /**
   * Track AI feature usage
   */
  private static async trackFeatureUsage(
    feature: 'paraphrase' | 'expand' | 'shrink' | 'summarize' | 'continue' | 'critic' | 'generate',
    metadata: {
      modelId?: string
      responseTime: number
      success: boolean
      tokens?: number
      error?: string
    }
  ): Promise<void> {
    try {
      await usageTracker.trackAIUsage({
        model: metadata.modelId || 'unknown',
        provider: 'openrouter', // Most AI calls go through OpenRouter
        tokens: metadata.tokens || 0,
        cost: 0, // Cost calculation can be added later if needed
        responseTime: metadata.responseTime,
        success: metadata.success,
        feature
      })
    } catch (error) {
      // Don't fail the AI operation if tracking fails
      console.warn('Failed to track AI feature usage:', error)
    }
  }
  
  /**
   * Get AI Analysis Service for reusing existing AI settings
   */
  private static getAIAnalysisService(): AIAnalysisService {
    if (!this.aiAnalysisService) {
      const settings = get(settingsStore)
      const aiSettings = settings.ai || {
        enabled: true,
        provider: 'openrouter' as const,
        selectedModel: 'openai/gpt-3.5-turbo',
        openaiApiKey: '',
        anthropicApiKey: '',
        googleApiKey: '',
        upstageApiKey: '',
        cerebrasApiKey: '',
        openrouterApiKey: '',
        groqApiKey: '',
        contextWindow: 4096,
        showTokenWarning: true,
        tokenWarningThreshold: 3000,
        maxTokens: 1000,
        temperature: 0.7
      }
      
      this.aiAnalysisService = new AIAnalysisService(aiSettings as any)
    }
    return this.aiAnalysisService
  }
  
  /**
   * Get current AI provider based on settings
   * This uses the same settings as AI Analysis
   */
  private static async getProvider(): Promise<AIProvider> {
    if (!this.provider) {
      const settings = get(settingsStore)
      const aiSettings = settings.ai || {
        enabled: true,
        provider: 'openrouter',
        selectedModel: 'openai/gpt-3.5-turbo',
        anthropicApiKey: '',
        openaiApiKey: '',
        googleApiKey: '',
        upstageApiKey: '',
        proxyUrl: ''
      }
      
      // Determine provider from selected model
      const selectedModel = aiSettings.selectedModel || 'openai/gpt-3.5-turbo'
      let provider: AIProviderConfig['provider'] = 'openrouter'
      let apiKey = ''
      
      // Check model prefix to determine correct provider
      if (selectedModel.startsWith('gpt-') || selectedModel.startsWith('o1-')) {
        provider = 'openai'
        apiKey = aiSettings.openaiApiKey || ''
      } else if (selectedModel.startsWith('claude-')) {
        provider = 'anthropic'
        apiKey = aiSettings.anthropicApiKey || ''
      } else if (selectedModel.startsWith('gemini-') || selectedModel.startsWith('models/gemini-')) {
        provider = 'google'
        apiKey = aiSettings.googleApiKey || ''
      } else if (selectedModel.startsWith('solar-')) {
        provider = 'upstage'
        apiKey = aiSettings.upstageApiKey || ''
      } else if (selectedModel.includes('/')) {
        // OpenRouter format: provider/model
        provider = 'openrouter'
      }
      
      // Determine provider config from settings
      const config: AIProviderConfig = {
        provider,
        apiKey: apiKey,
        model: selectedModel,
        proxyUrl: (aiSettings as any).proxyUrl || import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org'
      }
      
      // If OpenRouter or no API key, use proxy
      if (config.provider === 'openrouter' || !apiKey) {
        config.provider = 'openrouter'
        config.proxyUrl = config.proxyUrl || 'https://appstore.junlim.org'
      }
      
      this.provider = await AIProviderFactory.create(config)
    }
    return this.provider
  }
  
  /**
   * Get provider with specific model override
   */
  private static async getProviderWithModel(modelId: string): Promise<AIProvider> {
    // Check cache first
    if (this.providerCache.has(modelId)) {
      return this.providerCache.get(modelId)!
    }
    
    const settings = get(settingsStore)
    const aiSettings = settings.ai || {
      enabled: true,
      provider: 'openrouter',
      selectedModel: modelId,
      anthropicApiKey: '',
      openaiApiKey: '',
      googleApiKey: '',
      upstageApiKey: '',
      cerebrasApiKey: '',
      openrouterApiKey: '',
      groqApiKey: '',
      proxyUrl: ''
    }
    
    // Determine provider from model ID
    let provider: AIProviderConfig['provider'] = 'openrouter'
    let apiKey = ''
    
    // Check model prefix to determine provider
    if (modelId.startsWith('gpt-') || modelId.startsWith('o1-')) {
      provider = 'openai'
      apiKey = aiSettings.openaiApiKey || ''
    } else if (modelId.startsWith('claude-')) {
      provider = 'anthropic'
      apiKey = aiSettings.anthropicApiKey || ''
    } else if (modelId.startsWith('gemini-') || modelId.startsWith('models/gemini-')) {
      provider = 'google'
      apiKey = aiSettings.googleApiKey || ''
    } else if (modelId.startsWith('solar-')) {
      provider = 'upstage'
      apiKey = aiSettings.upstageApiKey || ''
    } else if (modelId.includes('/')) {
      // OpenRouter format: provider/model
      provider = 'openrouter'
    }
    
    // Create provider config
    const config: AIProviderConfig = {
      provider,
      apiKey,
      model: modelId,
      proxyUrl: (aiSettings as any).proxyUrl || import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org'
    }
    
    // If no API key and not OpenRouter, fall back to OpenRouter
    if (!apiKey && provider !== 'openrouter') {
      config.provider = 'openrouter'
      config.proxyUrl = config.proxyUrl || 'https://appstore.junlim.org'
    }
    
    const newProvider = await AIProviderFactory.create(config)
    
    // Cache the provider
    this.providerCache.set(modelId, newProvider)
    
    return newProvider
  }

  /**
   * Reset provider (when settings change)
   */
  static resetProvider() {
    this.provider = null
    this.providerCache.clear() // Clear cache when settings change
    this.aiAnalysisService = null
  }

  /**
   * Check if user is using their own API key (BYOK - Bring Your Own Key)
   * BYOK users should not be limited by server usage quotas
   */
  private static async isUsingUserOwnKey(modelId?: string): Promise<boolean> {
    try {
      const settings = get(settingsStore)
      const aiSettings = settings.ai

      if (!aiSettings) {
        return false
      }

      // Use provided modelId or get from settings
      const targetModel = modelId || aiSettings.selectedModel || ''

      // OpenRouter models (format: provider/model) always use server
      if (targetModel.includes('/')) {
        return false
      }

      // Check if user has entered their own API key for the model's provider
      if (targetModel.startsWith('gpt-') || targetModel.startsWith('o1-')) {
        return !!(aiSettings.openaiApiKey && aiSettings.openaiApiKey.trim().length > 0)
      }

      if (targetModel.startsWith('claude-')) {
        return !!(aiSettings.anthropicApiKey && aiSettings.anthropicApiKey.trim().length > 0)
      }

      if (targetModel.startsWith('gemini-') || targetModel.startsWith('models/gemini-')) {
        return !!(aiSettings.googleApiKey && aiSettings.googleApiKey.trim().length > 0)
      }

      if (targetModel.startsWith('solar-')) {
        return !!(aiSettings.upstageApiKey && aiSettings.upstageApiKey.trim().length > 0)
      }

      // Default: assume server-provided
      return false
    } catch (error) {
      console.warn('Failed to check BYOK status:', error)
      return false
    }
  }

  /**
   * Check AI feature limit before execution
   * Premium users and BYOK users are unlimited
   */
  private static async checkAIFeatureLimit(
    feature: 'paraphrase' | 'expand' | 'shrink' | 'summarize' | 'continue' | 'critic' | 'generate' | 'prompt',
    modelId?: string
  ): Promise<{ allowed: boolean; used?: number; limit?: number; remaining?: number }> {
    try {
      const subscription = get(subscriptionStore)
      const tier = subscription.type || 'free'

      // 1. Premium/Beta users → Unlimited (server-provided models)
      if (tier === 'premium' || tier === 'beta') {
        return { allowed: true }
      }

      // 2. BYOK (Bring Your Own Key) users → Unlimited
      // They pay for their own API usage, so no server quota limits
      const isBYOK = await this.isUsingUserOwnKey(modelId)
      if (isBYOK) {
        return { allowed: true }
      }

      // 3. Free/Trial users using server-provided OpenRouter models → Rate limited
      const tierLimits: Record<string, number> = {
        free: 10,
        trial: 50,
        premium: 2000,
        beta: 100
      }
      const limit = tierLimits[tier] || 10

      // Check current usage via rate limit check
      const limitCheck = await usageTracker.checkRateLimit('ai')

      if (!limitCheck.allowed) {
        // Parse usage from limitCheck if available
        const used = limitCheck.hourly?.used || limitCheck.daily?.used || limitCheck.minute?.used || limit
        const remaining = Math.max(0, limit - used)

        // Show upgrade prompt
        upgradePromptContext.set({
          reason: 'ai_feature_limit',
          details: JSON.stringify({
            feature,
            used,
            limit,
            remaining
          })
        })
        showUpgradePrompt.set(true)

        return { allowed: false, used, limit, remaining }
      }

      return { allowed: true }
    } catch (error) {
      console.warn('Failed to check AI feature limit:', error)
      // Allow operation on check failure to not block user
      return { allowed: true }
    }
  }

  /**
   * Paraphrase text - improve and change
   */
  static async paraphrase(text: string, options?: { style?: string; modelId?: string }): Promise<AIResponse> {
    const startTime = Date.now()

    // Check demo mode limits first
    this.checkDemoLimits('rewrite')

    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('paraphrase', options?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      const provider = options?.modelId
        ? await this.getProviderWithModel(options.modelId)
        : await this.getProvider()

      // Get combined prompt to pass as style option
      const combinedPrompt = await this.getCombinedPrompt()
      const enhancedOptions = {
        ...options,
        systemPrompt: combinedPrompt || undefined
      }

      const result = await provider.paraphrase(text, enhancedOptions)
      
      // Handle both string and AIResponseWithReason
      let processedResult: string
      let reason: string | undefined
      
      if (typeof result === 'string') {
        // Check if the string is JSON
        try {
          const parsed = JSON.parse(result)
          if (parsed.improved) {
            processedResult = parsed.improved
            reason = parsed.reason
          } else if (parsed.text) {
            processedResult = parsed.text
            reason = parsed.reason
          } else {
            processedResult = result
          }
        } catch {
          processedResult = result
        }
      } else if (result && typeof result === 'object') {
        if ('improved' in result) {
          processedResult = String((result as any).improved)
          reason = (result as any).reason
        } else if ('text' in result) {
          processedResult = String((result as any).text)
          reason = (result as any).reason
        } else {
          processedResult = String(result)
        }
      } else {
        processedResult = String(result)
      }
      
      // Clean up response if it contains context markers
      // More careful cleanup to preserve quotes
      processedResult = processedResult
        // Remove [대상: ...] patterns but preserve the content inside
        .replace(/\[대상:\s*([^\]]*)\]/gi, (match, content) => {
          // Preserve the content, especially if it starts/ends with quotes
          return content ? content.trim() : ''
        })
        // Remove incomplete [대상: patterns without closing bracket
        .replace(/\[대상:\s*(.*)$/gmi, (match, content) => {
          return content ? content.trim() : ''
        })
        // Remove standalone closing brackets at line ends
        .replace(/^\s*\]\s*$/gm, '')
        // Remove other context markers
        .replace(/\[수정\s*대상\][\s:]*/gi, '')
        .replace(/\[이전\][\s:]*/gi, '')
        .replace(/\[이후\][\s:]*/gi, '')
        // Remove prefix patterns
        .replace(/^대상[\s:]+/gmi, '')
        .replace(/^수정\s*대상[\s:]+/gmi, '')
        .replace(/^수정된?\s*텍스트[\s:]+/gmi, '')
        // Clean up any double spaces or line breaks left behind
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        .replace(/  +/g, ' ')
        .trim()

      // Track successful usage
      await this.trackFeatureUsage('paraphrase', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: true
      })

      return {
        success: true,
        result: processedResult,
        reason
      }
    } catch (error) {

      // Track failed usage
      await this.trackFeatureUsage('paraphrase', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      // Fall back to mock if real provider fails
      try {
        const result = await this.mockParaphrase(text)
        return {
          success: true,
          result
        }
      } catch {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  }
  
  /**
   * Paraphrase multiple segments
   */
  static async paraphraseSegments(segments: Segment[]): Promise<Segment[]> {
    try {
      // Process segments in parallel for better performance
      const promises = segments.map(async (segment) => {
        const response = await this.paraphrase(segment.original)
        const suggested = response.success ? (response.result as string) : segment.original
        
        // Use reason from API response if available, otherwise generate one
        const reason = response.reason || 
          (response.success && suggested !== segment.original 
            ? this.generateChangeReason(segment.original, suggested, 'paraphrase')
            : '')
        
        // Import TextSegmentService at the top of the file for this to work
        const preservedSuggested = segment.paragraphBreaks 
          ? suggested.includes('\n\n') 
            ? suggested // AI already preserved structure
            : suggested // Let TextSegmentService handle it in the component
          : suggested
        
        return {
          ...segment,
          suggested: preservedSuggested,
          reason,
          hasChanges: response.success && preservedSuggested !== segment.original,
          mode: 'paraphrase' as const,
          paragraphBreaks: segment.paragraphBreaks
        }
      })
      
      return await Promise.all(promises)
    } catch (error) {
      return segments
    }
  }
  
  /**
   * Generate reason for text changes
   */
  private static generateChangeReason(original: string, suggested: string, mode: string): string {
    // Simple heuristic-based reason generation
    if (mode === 'paraphrase') {
      if (suggested.length > original.length * 1.2) {
        return '문장을 더 명확하고 구체적으로 개선했습니다'
      } else if (suggested.length < original.length * 0.8) {
        return '불필요한 표현을 제거하여 간결하게 다듬었습니다'
      } else {
        return '가독성과 문장 흐름을 개선했습니다'
      }
    } else if (mode === 'expand') {
      return '세부 설명을 추가하여 내용을 보강했습니다'
    } else if (mode === 'shrink') {
      return '핵심 내용만 남기고 간결하게 정리했습니다'
    }
    return '문장을 개선했습니다'
  }
  
  /**
   * Expand text - make it longer and more detailed
   */
  static async expand(text: string, options?: { modelId?: string }): Promise<AIResponse> {
    const startTime = Date.now()

    // Check demo mode limits first
    this.checkDemoLimits('expand')

    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('expand', options?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      const provider = options?.modelId
        ? await this.getProviderWithModel(options.modelId)
        : await this.getProvider()

      // Get combined prompt to pass as context
      const combinedPrompt = await this.getCombinedPrompt()
      const contextOptions = combinedPrompt ? { systemPrompt: combinedPrompt } : undefined

      const result = await provider.expand(text, contextOptions)

      // Track successful usage
      await this.trackFeatureUsage('expand', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: true
      })

      // Handle both string and AIResponseWithReason
      if (typeof result === 'string') {
        return {
          success: true,
          result
        }
      } else {
        return {
          success: true,
          result: result.text,
          reason: result.reason
        }
      }
    } catch (error) {

      // Track failed usage
      await this.trackFeatureUsage('expand', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      try {
        const result = await this.mockExpand(text)
        return {
          success: true,
          result
        }
      } catch {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  }
  
  /**
   * Mock expand implementation
   */
  static async mockExpand(text: string): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    const sentences = text.split(/[.!?]+/).filter(s => s.trim())
    const expansions = sentences.map(sentence => {
      const trimmed = sentence.trim()
      if (!trimmed) return ''
      
      let expanded = trimmed
      if (trimmed.includes('중요')) {
        expanded += '. 이는 특히 주목할 만한 사항으로, 신중한 검토가 필요합니다'
      } else if (trimmed.includes('필요')) {
        expanded += '. 이러한 요구사항은 전체적인 맥락에서 매우 중요한 의미를 갖습니다'
      } else {
        expanded += '. 추가적으로, 이와 관련된 세부사항을 고려해야 합니다'
      }
      return expanded
    })
    
    return expansions.join('. ') + '.'
  }
  
  /**
   * Shrink text - make it shorter and more concise
   */
  static async shrink(text: string, options?: { modelId?: string }): Promise<AIResponse> {
    const startTime = Date.now()

    // Check demo mode limits first
    this.checkDemoLimits('summarize')

    // Check limit before execution
    const limitCheck = await this.checkAIFeatureLimit('shrink', options?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      const provider = options?.modelId
        ? await this.getProviderWithModel(options.modelId)
        : await this.getProvider()

      // Get combined prompt to pass as context
      const combinedPrompt = await this.getCombinedPrompt()
      const contextOptions = combinedPrompt ? { systemPrompt: combinedPrompt } : undefined

      const result = await provider.shrink(text, contextOptions)

      // Track successful usage
      await this.trackFeatureUsage('shrink', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: true
      })

      // Handle both string and AIResponseWithReason
      if (typeof result === 'string') {
        return {
          success: true,
          result
        }
      } else {
        return {
          success: true,
          result: result.text,
          reason: result.reason
        }
      }
    } catch (error) {

      // Track failed usage
      await this.trackFeatureUsage('shrink', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      try {
        const result = await this.mockShrink(text)
        return {
          success: true,
          result
        }
      } catch {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  }
  
  /**
   * Mock shrink implementation
   */
  static async mockShrink(text: string): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    let shortened = text
      .replace(/이러한\s+/g, '')
      .replace(/그러한\s+/g, '')
      .replace(/매우\s+/g, '')
      .replace(/추가적으로[,\s]+/g, '')
      .replace(/구체적으로[,\s]+/g, '')
    
    const sentences = shortened.split(/[.!?]+/).filter(s => s.trim())
    const keepCount = Math.max(1, Math.ceil(sentences.length * 0.6))
    const essential = sentences.slice(0, keepCount)
    
    return essential.join('. ') + '.'
  }
  
  /**
   * Summarize text
   */
  static async summarize(text: string, isSelection: boolean = false, options?: { modelId?: string }): Promise<AIResponse> {
    const startTime = Date.now()

    // Check demo mode limits first
    this.checkDemoLimits('summarize')

    // Check limit before execution
    const limitCheck = await this.checkAIFeatureLimit('summarize', options?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      const provider = options?.modelId
        ? await this.getProviderWithModel(options.modelId)
        : await this.getProvider()

      // Get combined prompt to pass as context
      const combinedPrompt = await this.getCombinedPrompt()
      const contextOptions = combinedPrompt ? { systemPrompt: combinedPrompt } : undefined

      const result = await provider.summarize(text, contextOptions)

      // Track successful usage
      await this.trackFeatureUsage('summarize', {
        responseTime: Date.now() - startTime,
        success: true
      })

      // Handle both string and AIResponseWithReason
      if (typeof result === 'string') {
        return {
          success: true,
          result
        }
      } else {
        return {
          success: true,
          result: result.text,
          reason: result.reason
        }
      }
    } catch (error) {

      // Track failed usage
      await this.trackFeatureUsage('summarize', {
        responseTime: Date.now() - startTime,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      try {
        const result = await this.mockSummarize(text, isSelection)
        return {
          success: true,
          result
        }
      } catch {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  }
  
  /**
   * Mock summarize implementation
   */
  static async mockSummarize(text: string, isSelection: boolean): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    const sentences = text.split(/[.!?]+/).filter(s => s.trim())
    
    if (sentences.length <= 2) {
      return text
    }
    
    const maxPoints = isSelection ? 3 : 5
    const keyPoints: string[] = []
    
    if (sentences[0]) {
      keyPoints.push(sentences[0].trim())
    }
    
    if (sentences.length > 2) {
      const middleIndex = Math.floor(sentences.length / 2)
      keyPoints.push(sentences[middleIndex].trim())
    }
    
    if (sentences.length > 1) {
      keyPoints.push(sentences[sentences.length - 1].trim())
    }
    
    const finalPoints = keyPoints.slice(0, maxPoints)
    return '핵심 요약: ' + finalPoints.join('. ') + '.'
  }
  
  /**
   * Process text with custom prompt with streaming support
   */
  static async processWithPromptStreaming(
    text: string,
    prompt: string,
    onChunk?: (chunk: string) => void,
    context?: {
      contextBefore?: string
      contextAfter?: string
      mode?: 'sentence' | 'paragraph'
      modelId?: string
    }
  ): Promise<AIResponse> {
    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('prompt', context?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      let provider = context?.modelId
        ? await this.getProviderWithModel(context.modelId)
        : await this.getProvider()
      
      // Get combined prompt (posting takes priority over system)
      const combinedPrompt = await this.getCombinedPrompt()
      
      // Build prompt for text modification
      let fullPrompt = ''
      
      // Add combined prompt if available
      if (combinedPrompt) {
        fullPrompt += `사용자 지정 스타일: ${combinedPrompt}\n\n`
      }
      
      fullPrompt += `다음 텍스트를 "${prompt}" 요청에 따라 수정해주세요.\n`
      fullPrompt += `중요: 원문의 마크다운 서식(볼드, 이탤릭, 코드, 취소선, 인용구, 구분선 등)을 유지하면서 내용만 수정하세요.\n\n`
      
      if (context?.contextBefore || context?.contextAfter) {
        fullPrompt += `전체 문맥:\n`
        if (context.contextBefore) {
          fullPrompt += `[이전] ${context.contextBefore}`
        }
        fullPrompt += `[수정 대상] ${text}`
        if (context.contextAfter) {
          fullPrompt += `[이후] ${context.contextAfter}`
        }
        fullPrompt += `\n\n`
      } else {
        fullPrompt += `원문: ${text}\n\n`
      }
      
      fullPrompt += `요청사항: ${prompt}\n\n`
      
      if (context?.mode === 'sentence') {
        fullPrompt += `한 문장으로 간결하게 작성하세요.\n`
      } else if (context?.mode === 'paragraph') {
        fullPrompt += `한 문단으로 자세하게 작성하세요.\n`
      }
      
      fullPrompt += `수정된 텍스트만 반환하세요. 마크다운 서식을 유지하고, 추가 설명이나 문맥 정보는 포함하지 마세요.`
      
      // Check if provider has processCustomPromptStreaming method
      const providerWithStreaming = provider as any
      if (providerWithStreaming.processCustomPromptStreaming && onChunk) {
        const result = await providerWithStreaming.processCustomPromptStreaming(
          text, 
          prompt, 
          context?.mode,
          onChunk
        )
        
        // Parse the response to extract only the modified text
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          processedResult = result
        } else {
          processedResult = result.text
          reason = result.reason
        }
        
        // More aggressive cleanup for various patterns (same as paraphrase method)
        processedResult = processedResult
          // Remove all variations of [대상: ...] patterns
          .replace(/\[대상:\s*([^\]]*)\]/gi, (match, content) => {
            return content || ''
          })
          // Remove incomplete [대상: patterns without closing bracket
          .replace(/\[대상:\s*(.*)$/gmi, (match, content) => {
            return content || ''
          })
          // Remove standalone closing brackets at line ends
          .replace(/^\s*\]\s*$/gm, '')
          // Remove other context markers
          .replace(/\[수정\s*대상\][\s:]*/gi, '')
          .replace(/\[이전\][\s:]*/gi, '')
          .replace(/\[이후\][\s:]*/gi, '')
          // Remove prefix patterns
          .replace(/^대상[\s:]+/gmi, '')
          .replace(/^수정\s*대상[\s:]+/gmi, '')
          .replace(/^수정된?\s*텍스트[\s:]+/gmi, '')
          // Clean up any double spaces or line breaks left behind
          .replace(/\n\s*\n\s*\n/g, '\n\n')
          .replace(/  +/g, ' ')
          .trim()
        
        // Remove request echo patterns
        if (processedResult.includes('요청사항:') || processedResult.includes('요청 사항:')) {
          const lines = processedResult.split('\n')
          const filteredLines = lines.filter(line => {
            const trimmed = line.trim()
            return !trimmed.startsWith('요청사항:') && 
                   !trimmed.startsWith('요청 사항:') &&
                   !trimmed.match(/^요청\s*사항\s*:/)
          })
          processedResult = filteredLines.join('\n').trim()
        }
        
        // Remove any remaining context markers and clean up
        processedResult = processedResult
          .replace(/전체 문맥:\s*/g, '')
          .replace(/원문:\s*[^\n]*\n/g, '')
          .replace(/수정된 텍스트:\s*/g, '')
          .replace(/^\s*[\[\]]\s*$/gm, '')
          .trim()
        
        return {
          success: true,
          result: processedResult,
          reason
        }
      } else {
        // Fall back to non-streaming version
        return await this.processWithPrompt(text, prompt, context)
      }
    } catch (error) {
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
  
  /**
   * Process text with custom prompt
   */
  static async processWithPrompt(
    text: string,
    prompt: string,
    context?: {
      contextBefore?: string
      contextAfter?: string
      mode?: 'sentence' | 'paragraph'
      modelId?: string
    }
  ): Promise<AIResponse> {
    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('prompt', context?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      let provider = context?.modelId
        ? await this.getProviderWithModel(context.modelId)
        : await this.getProvider()
      
      // Get combined prompt (posting takes priority over system)
      const combinedPrompt = await this.getCombinedPrompt()
      
      // Build prompt for text modification
      let fullPrompt = ''
      
      // Add combined prompt if available
      if (combinedPrompt) {
        fullPrompt += `사용자 지정 스타일: ${combinedPrompt}\n\n`
      }
      
      fullPrompt += `다음 텍스트를 "${prompt}" 요청에 따라 수정해주세요.\n`
      fullPrompt += `중요: 원문의 마크다운 서식(볼드, 이탤릭, 코드, 취소선, 인용구, 구분선 등)을 유지하면서 내용만 수정하세요.\n\n`
      
      if (context?.contextBefore || context?.contextAfter) {
        fullPrompt += `전체 문맥:\n`
        if (context.contextBefore) {
          fullPrompt += `[이전] ${context.contextBefore}`
        }
        fullPrompt += `[수정 대상] ${text}`
        if (context.contextAfter) {
          fullPrompt += `[이후] ${context.contextAfter}`
        }
        fullPrompt += `\n\n`
      } else {
        fullPrompt += `원문: ${text}\n\n`
      }
      
      fullPrompt += `요청사항: ${prompt}\n\n`
      
      if (context?.mode === 'sentence') {
        fullPrompt += `한 문장으로 간결하게 작성하세요.\n`
      } else if (context?.mode === 'paragraph') {
        fullPrompt += `한 문단으로 자세하게 작성하세요.\n`
      }
      
      fullPrompt += `수정된 텍스트만 반환하세요. 마크다운 서식을 유지하고, 추가 설명이나 문맥 정보는 포함하지 마세요.`
      
      // Try direct API first, then fallback to OpenRouter if it fails
      let result: any
      try {
        // Use processCustomPrompt if available, otherwise fallback to paraphrase
        const providerWithCustom = provider as any
        result = providerWithCustom.processCustomPrompt 
          ? await providerWithCustom.processCustomPrompt(text, prompt, context?.mode)
          : await provider.paraphrase(fullPrompt, { style: 'custom' })
      } catch (error: any) {
        // Check if error is due to credit/billing issues, service unavailable, or CORS
        if (error.message && (
          error.message.includes('credit') || 
          error.message.includes('billing') || 
          error.message.includes('400') ||
          error.message.includes('503') ||
          error.message.includes('overloaded') ||
          error.message.includes('UNAVAILABLE') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('CORS')
        )) {
          // API issue detected, falling back to OpenRouter
          
          // Determine the reason for fallback
          let fallbackReason = ''
          if (error.message.includes('credit') || error.message.includes('billing')) {
            fallbackReason = 'API 크레딧 부족'
          } else if (error.message.includes('503') || error.message.includes('overloaded')) {
            fallbackReason = '서비스 과부하'
          } else if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
            fallbackReason = '네트워크 연결 문제'
          } else {
            fallbackReason = 'API 오류'
          }
          
          // Show status message about fallback
          this.showStatus(
            `${fallbackReason}으로 인해 대체 서비스(OpenRouter)를 사용합니다.`,
            'warning'
          )
          
          // Create OpenRouter provider as fallback
          const fallbackConfig: AIProviderConfig = {
            provider: 'openrouter',
            apiKey: '',
            model: context?.modelId || 'openai/gpt-3.5-turbo',
            proxyUrl: import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org'
          }
          
          try {
            const fallbackProvider = await AIProviderFactory.create(fallbackConfig)
            
            const fallbackWithCustom = fallbackProvider as any
            result = fallbackWithCustom.processCustomPrompt 
              ? await fallbackWithCustom.processCustomPrompt(text, prompt, context?.mode)
              : await fallbackProvider.paraphrase(fullPrompt, { style: 'custom' })
          } catch (fallbackError) {
            // OpenRouter fallback also failed
            // If OpenRouter also fails, show error and return mock result
            this.showStatus(
              '모든 AI 서비스를 사용할 수 없습니다. 잠시 후 다시 시도해주세요.',
              'error'
            )
            // Use mock implementation as last resort
            const mockResult = await this.mockProcessWithPrompt(text, prompt, context?.mode)
            return {
              success: true,
              result: mockResult,
              reason: 'Mock 결과 (AI 서비스 불가)'
            }
          }
        } else {
          throw error // Re-throw if not a recoverable issue
        }
      }
      
      // Parse the response to extract only the modified text
      let processedResult: string
      let reason: string | undefined
      
      if (typeof result === 'string') {
        processedResult = result
      } else {
        processedResult = result.text
        reason = result.reason
      }
      
      // More aggressive cleanup for various patterns (same as paraphrase method)
      processedResult = processedResult
        // Remove all variations of [대상: ...] patterns
        .replace(/\[대상:\s*([^\]]*)\]/gi, (match, content) => {
          return content || ''
        })
        // Remove incomplete [대상: patterns without closing bracket
        .replace(/\[대상:\s*(.*)$/gmi, (match, content) => {
          return content || ''
        })
        // Remove standalone closing brackets at line ends
        .replace(/^\s*\]\s*$/gm, '')
        // Remove other context markers
        .replace(/\[수정\s*대상\][\s:]*/gi, '')
        .replace(/\[이전\][\s:]*/gi, '')
        .replace(/\[이후\][\s:]*/gi, '')
        // Remove prefix patterns
        .replace(/^대상[\s:]+/gmi, '')
        .replace(/^수정\s*대상[\s:]+/gmi, '')
        .replace(/^수정된?\s*텍스트[\s:]+/gmi, '')
        // Clean up any double spaces or line breaks left behind
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        .replace(/  +/g, ' ')
        .trim()
      
      // Remove request echo patterns (when AI echoes back the request)
      if (processedResult.includes('요청사항:') || processedResult.includes('요청 사항:')) {
        // Remove lines that echo the request
        const lines = processedResult.split('\n')
        const filteredLines = lines.filter(line => {
          const trimmed = line.trim()
          return !trimmed.startsWith('요청사항:') && 
                 !trimmed.startsWith('요청 사항:') &&
                 !trimmed.match(/^요청\s*사항\s*:/)
        })
        processedResult = filteredLines.join('\n').trim()
      }
      
      // Remove any prompt echo patterns if AI includes the original prompt
      if (prompt && processedResult.includes(prompt)) {
        // Try to extract just the response after the prompt
        const promptIndex = processedResult.indexOf(prompt)
        const afterPrompt = processedResult.substring(promptIndex + prompt.length).trim()
        if (afterPrompt && afterPrompt.length > 0) {
          processedResult = afterPrompt
        }
      }
      
      // Remove patterns where AI might echo the modification request
      const promptPatterns = [
        new RegExp(`"${prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}".*?[:\n]\\s*`, 'gi'),
        new RegExp(`${prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*?[:\n]\\s*`, 'gi'),
        /다음 텍스트를.*?수정해주세요[:\n]\s*/gi,
        /요청에 따라.*?수정.*?[:\n]\s*/gi
      ]
      
      for (const pattern of promptPatterns) {
        processedResult = processedResult.replace(pattern, '')
      }
      
      // Remove any remaining context markers and clean up
      processedResult = processedResult
        .replace(/전체 문맥:\s*/g, '')
        .replace(/원문:\s*[^\n]*\n/g, '') // Remove "원문:" lines
        .replace(/수정된 텍스트:\s*/g, '') // Remove "수정된 텍스트:" prefix
        .replace(/결과:\s*/g, '') // Remove "결과:" prefix
        .replace(/답변:\s*/g, '') // Remove "답변:" prefix
        .replace(/^\s*[\[\]]\s*$/gm, '') // Remove empty brackets on their own lines
        .trim()
      
      // If the result starts with quotes and ends with quotes, remove them
      if (processedResult.startsWith('"') && processedResult.endsWith('"')) {
        processedResult = processedResult.slice(1, -1).trim()
      }
      if (processedResult.startsWith("'") && processedResult.endsWith("'")) {
        processedResult = processedResult.slice(1, -1).trim()
      }
      
      return {
        success: true,
        result: processedResult,
        reason
      }
    } catch (error) {
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
  
  /**
   * Generate new text based on prompt
   */
  static async generate(
    prompt: string,
    context?: {
      beforeText?: string
      afterText?: string
      cursorPosition?: number
      mode?: 'sentence' | 'paragraph'
      modelId?: string
    }
  ): Promise<AIResponse> {
    const startTime = Date.now()

    // Check limit before execution
    const limitCheck = await this.checkAIFeatureLimit('generate', context?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      const provider = context?.modelId
        ? await this.getProviderWithModel(context.modelId)
        : await this.getProvider()
      
      // Check if provider has processCustomPrompt method
      const providerWithCustom = provider as any
      if (providerWithCustom.processCustomPrompt) {
        // Build context-aware prompt that includes surrounding text
        let contextualPrompt = prompt
        
        if (context?.beforeText || context?.afterText) {
          contextualPrompt = `${prompt}\n\n문맥 정보:\n`
          if (context.beforeText) {
            const trimmedBefore = context.beforeText.trim().slice(-100) // Last 100 chars for context
            if (trimmedBefore) {
              contextualPrompt += `[이전 문맥] ...${trimmedBefore}\n`
            }
          }
          if (context.afterText) {
            const trimmedAfter = context.afterText.trim().slice(0, 100) // First 100 chars for context
            if (trimmedAfter) {
              contextualPrompt += `[다음 문맥] ${trimmedAfter}...\n`
            }
          }
        }
        
        // Use processCustomPrompt with empty text for generation
        const result = await providerWithCustom.processCustomPrompt('', contextualPrompt, context?.mode)
        
        // Handle both string and AIResponseWithReason
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          processedResult = result
        } else {
          processedResult = result.text
          reason = result.reason
        }
        
        // Clean up any context markers that might leak through
        processedResult = processedResult
          .replace(/\[이전 문맥\][^[]*?(?=\[|$)/g, '')
          .replace(/\[다음 문맥\][^[]*?(?=\[|$)/g, '')
          .replace(/^문맥 정보:.*$/gm, '')
          .replace(/^\.\.\./gm, '')
          .trim()

        // Track successful usage
        await this.trackFeatureUsage('generate', {
          modelId: context?.modelId,
          responseTime: Date.now() - startTime,
          success: true
        })

        return {
          success: true,
          result: processedResult,
          reason
        }
      } else {
        // Fallback to old method for providers without processCustomPrompt
        // Build prompt for paraphrase-based generation
        let fullPrompt = ``
        
        if (context?.mode === 'sentence') {
          fullPrompt = `한 문장을 생성해주세요. `
        } else if (context?.mode === 'paragraph') {
          fullPrompt = `한 문단을 생성해주세요. `
        }
        
        fullPrompt += `요청사항: ${prompt}\n\n생성된 텍스트만 반환하세요.`
        
        const result = await provider.paraphrase(fullPrompt, { style: 'generate' })
        
        // Handle both string and AIResponseWithReason
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          processedResult = result
        } else {
          processedResult = result.text
          reason = result.reason
        }

        // Track successful usage
        await this.trackFeatureUsage('generate', {
          modelId: context?.modelId,
          responseTime: Date.now() - startTime,
          success: true
        })

        return {
          success: true,
          result: processedResult,
          reason
        }
      }
    } catch (error) {

      // Fall back to mock implementation
      try {
        const result = await this.mockGenerate(prompt, {
          beforeText: context?.beforeText,
          afterText: context?.afterText,
          mode: context?.mode
        })
        return {
          success: true,
          result
        }
      } catch {
        // Track failed usage
        await this.trackFeatureUsage('generate', {
          modelId: context?.modelId,
          responseTime: Date.now() - startTime,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        })

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  }

  /**
   * Paraphrase text with streaming support
   */
  static async paraphraseStreaming(
    text: string,
    onChunk?: (chunk: string) => void,
    modelId?: string
  ): Promise<AIResponse> {
    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('paraphrase', modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      const provider = modelId
        ? await this.getProviderWithModel(modelId)
        : await this.getProvider()
      
      // Get combined prompt (posting takes priority over system)
      const combinedPrompt = await this.getCombinedPrompt()
      
      // Check if provider has paraphraseStreaming method (new fast path)
      const providerWithStreaming = provider as any
      if (providerWithStreaming.paraphraseStreaming) {
        const result = await providerWithStreaming.paraphraseStreaming(text, onChunk)
        
        // Handle both string and AIResponseWithReason
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          // Check if the string is JSON
          try {
            const parsed = JSON.parse(result)
            if (parsed.improved) {
              processedResult = parsed.improved
              reason = parsed.reason
            } else if (parsed.text) {
              processedResult = parsed.text
              reason = parsed.reason
            } else {
              processedResult = result
            }
          } catch {
            processedResult = result
          }
        } else if (result && typeof result === 'object') {
          if ('improved' in result) {
            processedResult = result.improved
            reason = result.reason
          } else if ('text' in result) {
            processedResult = result.text
            reason = result.reason
          } else {
            processedResult = String(result)
          }
        } else {
          processedResult = String(result)
        }
        
        return {
          success: true,
          result: processedResult,
          reason
        }
      } else {
        // Fall back to non-streaming version
        return await this.paraphrase(text, modelId ? { modelId } : undefined)
      }
    } catch (error) {
      return await this.paraphrase(text, modelId ? { modelId } : undefined)
    }
  }

  /**
   * Expand text with streaming support
   */
  static async expandStreaming(
    text: string,
    onChunk?: (chunk: string) => void,
    modelId?: string
  ): Promise<AIResponse> {
    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('expand', modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      const provider = modelId
        ? await this.getProviderWithModel(modelId)
        : await this.getProvider()
      
      // Get combined prompt (posting takes priority over system)
      const combinedPrompt = await this.getCombinedPrompt()
      
      // Check if provider has expandStreaming method (new fast path)
      const providerWithStreaming = provider as any
      if (providerWithStreaming.expandStreaming) {
        const result = await providerWithStreaming.expandStreaming(text, onChunk)
        
        // Handle both string and AIResponseWithReason
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          // Check if the string is JSON
          try {
            const parsed = JSON.parse(result)
            if (parsed.improved) {
              processedResult = parsed.improved
              reason = parsed.reason
            } else if (parsed.text) {
              processedResult = parsed.text
              reason = parsed.reason
            } else {
              processedResult = result
            }
          } catch {
            processedResult = result
          }
        } else if (result && typeof result === 'object') {
          if ('improved' in result) {
            processedResult = result.improved
            reason = result.reason
          } else if ('text' in result) {
            processedResult = result.text
            reason = result.reason
          } else {
            processedResult = String(result)
          }
        } else {
          processedResult = String(result)
        }
        
        return {
          success: true,
          result: processedResult,
          reason
        }
      } else {
        // Fall back to non-streaming version
        return await this.expand(text, modelId ? { modelId } : undefined)
      }
    } catch (error) {
      return await this.expand(text, modelId ? { modelId } : undefined)
    }
  }

  /**
   * Shrink text with streaming support
   */
  static async shrinkStreaming(
    text: string,
    onChunk?: (chunk: string) => void,
    modelId?: string
  ): Promise<AIResponse> {
    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('shrink', modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      const provider = modelId
        ? await this.getProviderWithModel(modelId)
        : await this.getProvider()
      
      // Get combined prompt (posting takes priority over system)
      const combinedPrompt = await this.getCombinedPrompt()
      
      // Check if provider has shrinkStreaming method (new fast path)
      const providerWithStreaming = provider as any
      if (providerWithStreaming.shrinkStreaming) {
        const result = await providerWithStreaming.shrinkStreaming(text, onChunk)
        
        // Handle both string and AIResponseWithReason
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          // Check if the string is JSON
          try {
            const parsed = JSON.parse(result)
            if (parsed.improved) {
              processedResult = parsed.improved
              reason = parsed.reason
            } else if (parsed.text) {
              processedResult = parsed.text
              reason = parsed.reason
            } else {
              processedResult = result
            }
          } catch {
            processedResult = result
          }
        } else if (result && typeof result === 'object') {
          if ('improved' in result) {
            processedResult = result.improved
            reason = result.reason
          } else if ('text' in result) {
            processedResult = result.text
            reason = result.reason
          } else {
            processedResult = String(result)
          }
        } else {
          processedResult = String(result)
        }
        
        return {
          success: true,
          result: processedResult,
          reason
        }
      } else {
        // Fall back to non-streaming version
        return await this.shrink(text, modelId ? { modelId } : undefined)
      }
    } catch (error) {
      return await this.shrink(text, modelId ? { modelId } : undefined)
    }
  }

  /**
   * Summary text with streaming support
   */
  static async summaryStreaming(
    text: string,
    onChunk?: (chunk: string) => void,
    modelId?: string
  ): Promise<AIResponse> {
    try {
      const provider = modelId 
        ? await this.getProviderWithModel(modelId)
        : await this.getProvider()
      
      // Get combined prompt (posting takes priority over system)
      const combinedPrompt = await this.getCombinedPrompt()
      
      // Check if provider has processCustomPromptStreaming method
      const providerWithStreaming = provider as any
      if (providerWithStreaming.processCustomPromptStreaming) {
        let prompt = '다음 텍스트를 읽고 핵심 내용을 요약해주세요. 원문의 주요 포인트를 놓치지 않으면서도 간결하게 정리해주세요.\n\n'
        
        if (combinedPrompt) {
          prompt = `사용자 지정 스타일: ${combinedPrompt}\n\n${prompt}`
        }
        
        const result = await providerWithStreaming.processCustomPromptStreaming(text, prompt, undefined, onChunk)
        
        // Handle both string and AIResponseWithReason
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          // Check if the string is JSON
          try {
            const parsed = JSON.parse(result)
            if (parsed.improved) {
              processedResult = parsed.improved
              reason = parsed.reason
            } else if (parsed.text) {
              processedResult = parsed.text
              reason = parsed.reason
            } else {
              processedResult = result
            }
          } catch {
            processedResult = result
          }
        } else if (result && typeof result === 'object') {
          if ('improved' in result) {
            processedResult = result.improved
            reason = result.reason
          } else if ('text' in result) {
            processedResult = result.text
            reason = result.reason
          } else {
            processedResult = String(result)
          }
        } else {
          processedResult = String(result)
        }
        
        return {
          success: true,
          result: processedResult,
          reason: reason || '핵심 내용을 요약했습니다'
        }
      } else {
        // Fall back to non-streaming version
        return await this.summarize(text)
      }
    } catch (error) {
      return await this.summarize(text)
    }
  }

  /**
   * Generate text from prompt with streaming support
   */
  static async generateStreaming(
    prompt: string,
    context?: {
      beforeText?: string
      afterText?: string
      cursorPosition?: number
      mode?: 'sentence' | 'paragraph'
      modelId?: string
    },
    onChunk?: (chunk: string) => void
  ): Promise<AIResponse> {
    const startTime = Date.now()

    // Check limit before execution
    const limitCheck = await this.checkAIFeatureLimit('generate', context?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      const provider = context?.modelId
        ? await this.getProviderWithModel(context.modelId)
        : await this.getProvider()
      
      // Get combined prompt (posting takes priority over system)
      const combinedPrompt = await this.getCombinedPrompt()
      
      // Check if provider has processCustomPromptStreaming method
      const providerWithStreaming = provider as any
      if (providerWithStreaming.processCustomPromptStreaming) {
        // Build context-aware prompt that includes surrounding text
        let contextualPrompt = ''
        
        // Add combined prompt if available
        if (combinedPrompt) {
          contextualPrompt += `사용자 지정 스타일: ${combinedPrompt}\n\n`
        }
        
        contextualPrompt += prompt
        contextualPrompt += `\n\n필요시 적절한 마크다운 서식(볼드, 이탤릭, 코드, 목록 등)을 사용하여 가독성 있게 작성하세요.`
        
        if (context?.beforeText || context?.afterText) {
          contextualPrompt += `\n\n문맥 정보:\n`
          if (context.beforeText) {
            const trimmedBefore = context.beforeText.trim().slice(-100) // Last 100 chars for context
            if (trimmedBefore) {
              contextualPrompt += `[이전 문맥] ...${trimmedBefore}\n`
            }
          }
          if (context.afterText) {
            const trimmedAfter = context.afterText.trim().slice(0, 100) // First 100 chars for context
            if (trimmedAfter) {
              contextualPrompt += `[다음 문맥] ${trimmedAfter}...\n`
            }
          }
        }
        
        // Use processCustomPromptStreaming with empty text for generation
        const result = await providerWithStreaming.processCustomPromptStreaming('', contextualPrompt, context?.mode, onChunk)
        
        // Handle both string and AIResponseWithReason
        let processedResult: string
        let reason: string | undefined
        
        if (typeof result === 'string') {
          processedResult = result
        } else {
          processedResult = result.text
          reason = result.reason
        }
        
        // Clean up context markers from generated text if they exist
        processedResult = processedResult
          .replace(/\[이전 문맥\][^[]*?(?=\[|$)/g, '')
          .replace(/\[다음 문맥\][^[]*?(?=\[|$)/g, '')
          .replace(/^문맥 정보:.*$/gm, '')
          .replace(/^\.\.\./gm, '')
          .trim()

        // Track successful usage
        await this.trackFeatureUsage('generate', {
          modelId: context?.modelId,
          responseTime: Date.now() - startTime,
          success: true
        })

        return {
          success: true,
          result: processedResult,
          reason
        }
      } else {
        // Fall back to non-streaming version if provider doesn't support streaming
        // Provider does not support streaming, falling back to non-streaming generate
        return await this.generate(prompt, context)
      }
    } catch (error) {

      // Fall back to non-streaming version
      try {
        return await this.generate(prompt, context)
      } catch {
        // Track failed usage
        await this.trackFeatureUsage('generate', {
          modelId: context?.modelId,
          responseTime: Date.now() - startTime,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        })

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  }

  /**
   * Mock process with prompt implementation
   */
  static async mockProcessWithPrompt(
    text: string,
    prompt: string,
    mode?: 'sentence' | 'paragraph'
  ): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // Check if this is generation (empty text) or modification
    const isGeneration = !text || text.trim() === ''
    const lowerPrompt = prompt.toLowerCase()
    
    if (isGeneration) {
      // Generate new text based on prompt
      if (mode === 'sentence') {
        if (lowerPrompt.includes('인사')) {
          return '안녕하세요, 반갑습니다.'
        } else if (lowerPrompt.includes('감사')) {
          return '도움을 주셔서 감사합니다.'
        } else {
          return `${prompt}에 대한 문장입니다.`
        }
      } else if (mode === 'paragraph') {
        if (lowerPrompt.includes('인사')) {
          return '안녕하세요! 만나서 반갑습니다. 오늘 좋은 하루 보내고 계신가요? 앞으로 잘 부탁드립니다.'
        } else if (lowerPrompt.includes('감사')) {
          return '도움을 주셔서 진심으로 감사드립니다. 덕분에 문제를 해결할 수 있었습니다. 앞으로도 좋은 관계를 유지하며 함께 성장해 나가기를 희망합니다.'
        } else {
          return `${prompt}에 대해 설명드리겠습니다. 이 내용은 중요한 의미를 담고 있습니다. 자세한 사항은 추가적인 검토가 필요할 수 있습니다.`
        }
      } else {
        return `${prompt}에 대한 내용을 생성했습니다.`
      }
    } else {
      // Modify existing text based on prompt
      if (lowerPrompt.includes('정중') || lowerPrompt.includes('공손')) {
        return text.replace(/해/g, '해요').replace(/다\./g, '습니다.')
      } else if (lowerPrompt.includes('간결')) {
        const sentences = text.split(/[.!?]+/).filter(s => s.trim())
        return sentences.slice(0, Math.ceil(sentences.length * 0.6)).join('. ') + '.'
      } else if (lowerPrompt.includes('자세')) {
        return text + ' 이에 대해 추가적으로 설명하면, 더 깊이 있는 이해가 가능합니다.'
      } else {
        // Default: slight modification
        return text + ' (수정됨)'
      }
    }
  }
  
  /**
   * Mock generate implementation
   */
  static async mockGenerate(
    prompt: string,
    context?: {
      beforeText?: string
      afterText?: string
      mode?: 'sentence' | 'paragraph'
    }
  ): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // Simple mock generation based on common prompts
    const lowerPrompt = prompt.toLowerCase()
    
    if (context?.mode === 'sentence') {
      if (lowerPrompt.includes('인사')) {
        return '안녕하세요, 반갑습니다.'
      } else if (lowerPrompt.includes('감사')) {
        return '도움을 주셔서 감사합니다.'
      } else {
        return `${prompt}에 대한 문장입니다.`
      }
    } else if (context?.mode === 'paragraph') {
      if (lowerPrompt.includes('인사')) {
        return '안녕하세요! 만나서 반갑습니다. 오늘 좋은 하루 보내고 계신가요? 앞으로 잘 부탁드립니다.'
      } else if (lowerPrompt.includes('감사')) {
        return '도움을 주셔서 진심으로 감사드립니다. 덕분에 문제를 해결할 수 있었습니다. 앞으로도 좋은 관계를 유지하며 함께 성장해 나가기를 희망합니다.'
      } else {
        return `${prompt}에 대해 설명드리겠습니다. 이 내용은 중요한 의미를 담고 있습니다. 자세한 사항은 추가적인 검토가 필요할 수 있습니다.`
      }
    } else {
      // Default behavior
      if (lowerPrompt.includes('인사') || lowerPrompt.includes('안녕')) {
        return '안녕하세요! 만나서 반갑습니다.'
      } else if (lowerPrompt.includes('감사')) {
        return '도움을 주셔서 진심으로 감사드립니다.'
      } else if (lowerPrompt.includes('소개')) {
        return '저는 한국어 문법 검사 도구입니다. 텍스트의 맞춤법과 문법을 검사하고 개선 제안을 제공합니다.'
      } else if (lowerPrompt.includes('설명')) {
        return '이 기능은 사용자가 요청한 내용에 따라 적절한 텍스트를 생성합니다. AI 모델을 활용하여 문맥에 맞는 자연스러운 문장을 만들어냅니다.'
      } else {
        return `${prompt}에 대한 내용을 생성했습니다.`
      }
    }
  }
  
  /**
   * Continue writing with streaming support
   */
  static async continueWritingStreaming(
    text: string,
    onChunk: (chunk: string) => void,
    options?: {
      cursorPosition?: number
      mode?: 'sentence' | 'paragraph'
      modelId?: string
      context?: {
        position?: string
        relativePosition?: string
        sectionTitle?: string
        documentType?: string
        paragraphPattern?: any
      }
    }
  ): Promise<AIResponse> {
    // Check limit before execution (pass modelId for BYOK check)
    const limitCheck = await this.checkAIFeatureLimit('continue', options?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      const provider = options?.modelId
        ? await this.getProviderWithModel(options.modelId)
        : await this.getProvider()

      const contextObj = {
        cursorPosition: options?.cursorPosition,
        mode: options?.mode || 'sentence',
        ...options?.context
      }

      // Debug: Log request at AIService level
      console.log('[AIService] continueWritingStreaming request:', {
        textLength: text.length,
        textPreview: text.substring(Math.max(0, text.length - 100)),  // Last 100 chars
        mode: contextObj.mode,
        modelId: options?.modelId,
        hasContext: !!options?.context,
        contextKeys: options?.context ? Object.keys(options.context) : [],
        providerName: (provider as any)?.name || 'unknown'
      })

      // Check if provider supports streaming
      if (provider.continueStreaming) {
        const startTime = Date.now()
        const result = await provider.continueStreaming(text, contextObj, onChunk)

        // Debug: Log response at AIService level
        console.log('[AIService] continueWritingStreaming response:', {
          success: true,
          responseTime: Date.now() - startTime,
          resultType: typeof result,
          resultLength: typeof result === 'string' ? result.length : (result as any)?.text?.length || 0,
          resultPreview: typeof result === 'string'
            ? result.substring(0, 100) + (result.length > 100 ? '...' : '')
            : ((result as any)?.text?.substring(0, 100) || '') + (((result as any)?.text?.length || 0) > 100 ? '...' : '')
        })
        
        if (typeof result === 'string') {
          return {
            success: true,
            result
          }
        } else {
          return {
            success: true,
            result: result.text,
            reason: result.reason
          }
        }
      } else {
        // Fallback to non-streaming
        const result = await provider.continue?.(text, contextObj)
        
        if (!result) {
          throw new Error('Provider does not support continuation')
        }
        
        // Call onChunk with the full result at once
        if (typeof result === 'string') {
          onChunk(result)
          return {
            success: true,
            result
          }
        } else {
          onChunk(result.text)
          return {
            success: true,
            result: result.text,
            reason: result.reason
          }
        }
      }
    } catch (error) {
      // Debug: Log error at AIService level
      console.error('[AIService] continueWritingStreaming error:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        textLength: text.length,
        mode: options?.mode,
        modelId: options?.modelId
      })

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Continue writing
   */
  static async continueWriting(
    text: string,
    options?: {
      cursorPosition?: number
      mode?: 'sentence' | 'paragraph'
      modelId?: string
      context?: {
        position?: string
        relativePosition?: string
        sectionTitle?: string
        documentType?: string
      }
    }
  ): Promise<AIResponse> {
    const startTime = Date.now()

    // Check demo mode limits first
    this.checkDemoLimits('expand')

    // Check limit before execution
    const limitCheck = await this.checkAIFeatureLimit('continue', options?.modelId)
    if (!limitCheck.allowed) {
      return {
        success: false,
        error: 'AI 기능 사용 한도에 도달했습니다.'
      }
    }

    try {
      // Get provider with optional model override
      const provider = options?.modelId
        ? await this.getProviderWithModel(options.modelId)
        : await this.getProvider()
      const contextObj = {
        cursorPosition: options?.cursorPosition,
        mode: options?.mode || 'sentence',
        ...options?.context
      }
      const result = await provider.continue?.(text, contextObj)

      if (!result) {
        throw new Error('Provider does not support continuation')
      }

      // Track successful usage
      await this.trackFeatureUsage('continue', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: true
      })

      // Handle both string and AIResponseWithReason
      if (typeof result === 'string') {
        return {
          success: true,
          result
        }
      } else {
        return {
          success: true,
          result: result.text,
          reason: result.reason
        }
      }
    } catch (error) {

      // Track failed usage
      await this.trackFeatureUsage('continue', {
        modelId: options?.modelId,
        responseTime: Date.now() - startTime,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      // Fall back to mock implementation
      try {
        const result = await this.mockContinue(text, options)
        return {
          success: true,
          result
        }
      } catch {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  }
  
  /**
   * Mock continue implementation
   */
  static async mockContinue(
    text: string, 
    options?: {
      cursorPosition?: number
      mode?: 'sentence' | 'paragraph'
    }
  ): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    const mode = options?.mode || 'sentence'
    const cursorPos = options?.cursorPosition ?? text.length
    const beforeCursor = text.substring(0, cursorPos)
    const afterCursor = text.substring(cursorPos)
    
    if (mode === 'paragraph') {
      return '이에 따라 추가적인 고려사항들이 필요합니다. 특히 실제 구현 단계에서는 여러 가지 변수들을 세심하게 검토해야 하며, 각 단계별로 적절한 검증 과정을 거쳐야 합니다. 또한 향후 확장 가능성을 염두에 두고 유연한 구조를 유지하는 것이 중요합니다.'
    } else {
      return '이를 통해 더 나은 결과를 얻을 수 있습니다.'
    }
  }
  
  /**
   * Get critique/feedback
   */
  static async critique(text: string): Promise<AIResponse> {
    try {
      const response = await fetch(`${this.apiEndpoint}/critique`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          options: {
            aspects: ['clarity', 'grammar', 'structure', 'coherence'],
            suggestions: true
          }
        })
      })
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }
      
      const data = await response.json()
      return {
        success: true,
        result: data.feedback
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
  
  /**
   * Mock implementation for development
   */
  static async mockParaphrase(text: string): Promise<string> {
    // Simple mock: just add "개선된: " prefix and slightly modify
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API delay
    
    // Korean text improvement simulation
    const improvements: Record<string, string> = {
      '입니다': '입니다',
      '있습니다': '있습니다',
      '합니다': '합니다',
      '됩니다': '됩니다',
      '했습니다': '했습니다',
      '것': '것',
      '를': '를',
      '을': '을',
      '이': '이',
      '가': '가'
    }
    
    let improved = text
    
    // Add some variation
    if (text.length > 50) {
      improved = text
        .replace(/그리고/g, '또한')
        .replace(/하지만/g, '그러나')
        .replace(/때문에/g, '로 인해')
    }
    
    // Add a subtle change to show it's working
    if (improved === text) {
      improved = text.replace(/\./g, '. ')
        .replace(/  +/g, ' ')
        .trim()
    }
    
    return improved
  }
  
  /**
   * Check if API is available
   * Since we're using real AI providers now, always return true
   */
  static async checkAvailability(): Promise<boolean> {
    // We're using real AI providers (OpenRouter proxy or direct API calls)
    // The mock API endpoints are not needed anymore
    return true
  }
}